import classNames from 'classnames';
import { memo } from 'react';
import type { ObjectAxisResize, ObjectGroup, ObjectUniformScale } from 'shared-types';
import { T, useT } from '../../../translation/src';
import { updateGroup } from '../document/specOps';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { Tooltip } from '../../../components/Tooltip';
import { FieldRow, HintLabel, PanelSection, Segmented, SelectField, TextField, ToggleRow } from '../panels/fields';
import { ModifierStack } from './ModifierStack';

/** Picture of a resize rule: the object's outline (dashed) and where the part goes when it grows. */
const RESIZE_ICON_PARTS: Record<ObjectAxisResize, React.ReactNode> = {
  stretch: (
    <>
      <rect x={4} y={9} width={16} height={6} rx={1} />
      <path d="M7 12h10M7 12l2-2M7 12l2 2M17 12l-2-2M17 12l-2 2" />
    </>
  ),
  fixed: <rect x={9} y={9} width={6} height={6} rx={1} />,
  pinStart: (
    <>
      <path d="M2 6v12" strokeWidth={2.5} />
      <rect x={4} y={9} width={6} height={6} rx={1} />
    </>
  ),
  pinEnd: (
    <>
      <path d="M22 6v12" strokeWidth={2.5} />
      <rect x={14} y={9} width={6} height={6} rx={1} />
    </>
  ),
  float: (
    <>
      <path d="M4 12h5M7 10l2 2-2 2" />
      <rect x={12} y={9} width={6} height={6} rx={1} />
    </>
  ),
};

const ResizeIcon: React.FC<{ rule: ObjectAxisResize; axis: 'u' | 'n' }> = ({ rule, axis }) => (
  // Depth: turned so "start" is the back (down on the canvas).
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={classNames('mx-auto h-5 w-5', axis === 'n' && '-rotate-90')}>
    <rect x={2} y={6} width={20} height={12} rx={1.5} strokeDasharray="2 2" strokeWidth={1} opacity={0.6} />
    {RESIZE_ICON_PARTS[rule]}
  </svg>
);

const RESIZE_RULES: readonly ObjectAxisResize[] = ['stretch', 'fixed', 'pinStart', 'pinEnd', 'float'];

/** Per-axis resize rule: five picture buttons, the chosen one spelled out underneath (left/right vs back/front words). */
const ResizeField: React.FC<{ axis: 'u' | 'n'; value: ObjectAxisResize; onChange: (v: ObjectAxisResize) => void }> = ({ axis, value, onChange }) => {
  const labels = {
    stretch: useT('stretches with the object', { swc: true }),
    fixed: useT('keeps its size, stays centered', { swc: true }),
    pinStart: useT(axis === 'u' ? 'keeps its size and its distance to the left end' : 'keeps its size and its distance to the back', { swc: true }),
    pinEnd: useT(axis === 'u' ? 'keeps its size and its distance to the right end' : 'keeps its size and its distance to the front', { swc: true }),
    float: useT(axis === 'u' ? 'keeps its size and its relative spot, e.g. a sink a third of the way along a counter' : 'keeps its size and its relative spot between back and front', { swc: true }),
  } satisfies Record<ObjectAxisResize, string>;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-sm">
        {/* What the chosen rule does: on hover (each button says its own on hover too). */}
        <HintLabel hint={labels[value]} className="w-12 shrink-0 text-gray-600 dark:text-gray-300">
          {axis === 'u' ? <T _str="width" swc /> : <T _str="depth" swc />}
        </HintLabel>
        <span className="min-w-0 flex-1">
          <Segmented value={value} options={RESIZE_RULES.map((rule) => ({ value: rule, title: labels[rule], label: <ResizeIcon rule={rule} axis={axis} /> }))} onChange={onChange} />
        </span>
      </div>
    </div>
  );
};

const UNIFORM_RULES: readonly ObjectUniformScale[] = ['width', 'depth', 'fit', 'fill'];

/** Proportional scaling: what the one scale factor follows, with the chosen rule spelled out. */
const UniformField: React.FC<{ value: ObjectUniformScale; onChange: (v: ObjectUniformScale) => void }> = ({ value, onChange }) => {
  const labels = {
    width: useT('width', { swc: true }),
    depth: useT('depth', { swc: true }),
    fit: useT('fit', { swc: true }),
    fill: useT('fill', { swc: true }),
  } satisfies Record<ObjectUniformScale, string>;
  const hints = {
    width: useT('scales with the width of the object', { swc: true }),
    depth: useT('scales with the depth of the object', { swc: true }),
    fit: useT('scales with the side that grew least, so it always fits inside', { swc: true }),
    fill: useT('scales with the side that grew most, so it always fills', { swc: true }),
  } satisfies Record<ObjectUniformScale, string>;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-sm">
        <HintLabel hint={hints[value]} className="w-12 shrink-0 text-gray-600 dark:text-gray-300">
          <T _str="follows" swc />
        </HintLabel>
        <span className="min-w-0 flex-1">
          <Segmented value={value} options={UNIFORM_RULES.map((rule) => ({ value: rule, label: labels[rule], title: hints[rule] }))} onChange={onChange} />
        </span>
      </div>
    </div>
  );
};

/**
 * When the group is drawn: always, or only while an on/off or choice setting
 * has one value — edited as the sentence it is ("only when [setting] is [on]").
 */
const VisibilityFields: React.FC<{ group: ObjectGroup; update: (g: ObjectGroup) => void }> = ({ group, update }) => {
  const { spec } = useEditorState();
  const onLabel = useT('on', { swc: false });
  const offLabel = useT('off', { swc: false });
  const onlyWhenHint = useT('draw this group only while an on/off or choice parameter has a given value', { swc: true });
  const noSettingsHint = useT('add an on/off or choice parameter to show this group only sometimes', { swc: true });
  const conditional = spec.params.filter((p) => p.kind === 'boolean' || p.kind === 'select');
  const param = conditional.find((p) => p.key === group.visibleWhen?.param);
  const always = () => {
    const { visibleWhen: _drop, ...rest } = group;
    update(rest);
  };
  const showWhen = (key: string) => {
    const next = conditional.find((p) => p.key === key);
    if (next) update({ ...group, visibleWhen: { param: key, equals: next.kind === 'select' ? next.default : true } });
  };
  const equals = group.visibleWhen?.equals;
  const valueLabel = param?.kind === 'select' ? (param.options.find((o) => o.value === equals)?.label ?? String(equals)) : equals === false ? offLabel : onLabel;
  // Without an on/off or choice parameter there is nothing to depend on: the switch says how to get one.
  const noSettings = conditional.length === 0;
  return (
    <>
      <ToggleRow label={<T _str="only when…" swc />} hint={noSettings ? noSettingsHint : onlyWhenHint} checked={!!param} onChange={(on) => (!on ? always() : !noSettings && showWhen(conditional[0].key))} />
      {param && (
        <Tooltip
          message={
            <span className="block max-w-64 text-xs">
              <T _str="drawn only while “{setting}” is {value}; hidden otherwise" args={{ setting: param.label, value: valueLabel }} />
            </span>
          }
          delay={300}
          wrapperClassName="w-full"
        >
          <div className="flex items-center gap-2 text-sm">
            <span className="min-w-0 flex-1">
              <SelectField value={param.key} options={conditional.map((p) => ({ value: p.key, label: p.label }))} onChange={showWhen} />
            </span>
            <span className="shrink-0 text-gray-600 dark:text-gray-300">
              <T _str="is" />
            </span>
            <span className="w-28 shrink-0">
              {param.kind === 'boolean' ? (
                <Segmented
                  value={equals === false ? 'off' : 'on'}
                  options={[
                    { value: 'on', label: onLabel },
                    { value: 'off', label: offLabel },
                  ]}
                  onChange={(v) => update({ ...group, visibleWhen: { param: param.key, equals: v === 'on' } })}
                />
              ) : param.kind === 'select' ? (
                <SelectField value={String(equals ?? param.default)} options={param.options.map((o) => ({ value: o.value, label: o.label }))} onChange={(v) => update({ ...group, visibleWhen: { param: param.key, equals: v } })} />
              ) : null}
            </span>
          </div>
        </Tooltip>
      )}
    </>
  );
};

/** The selected group: its resize behaviour, modifier stack (repeat, mirror, round corners) and visibility rules. */
const GroupInspector_: React.FC<{ id: string }> = ({ id }) => {
  const { store } = useObjectEditor();
  const { spec } = useEditorState();
  const group = spec.groups.find((g) => g.id === id);
  if (!group) return null;
  const update = (next: ObjectGroup) => store.apply((s) => updateGroup(s, id, () => next));

  return (
    <>
      <PanelSection title={<T _str="group" />}>
        <FieldRow label={<T _str="name" swc />}>
          <TextField value={group.name} maxLength={80} onCommit={(name) => name.trim() && update({ ...group, name: name.trim() })} />
        </FieldRow>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          <T _str="{count} shapes" args={{ count: group.shapeIds.length }} />
        </p>
      </PanelSection>

      <PanelSection title={<T _str="when the object is resized" />}>
        <ToggleRow
          label={<T _str="keep proportions" swc />}
          checked={!!group.resize.uniform}
          onChange={(on) => {
            const { uniform: _drop, ...perAxis } = group.resize;
            update({ ...group, resize: on ? { ...perAxis, uniform: 'fit' } : perAxis });
          }}
        />
        {group.resize.uniform ? (
          <UniformField value={group.resize.uniform} onChange={(uniform) => update({ ...group, resize: { ...group.resize, uniform } })} />
        ) : (
          <>
            <ResizeField axis="u" value={group.resize.u} onChange={(u) => update({ ...group, resize: { ...group.resize, u } })} />
            <ResizeField axis="n" value={group.resize.n} onChange={(n) => update({ ...group, resize: { ...group.resize, n } })} />
          </>
        )}
      </PanelSection>

      <ModifierStack group={group} />

      <PanelSection title={<T _str="shown" />}>
        <VisibilityFields group={group} update={update} />
      </PanelSection>

      {(group.offset || group.visibleIf) && (
        <p className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
          <T _str="this group is placed by a rule (see the Rules list)" swc />
        </p>
      )}
    </>
  );
};

export const GroupInspector = memo(GroupInspector_);
